import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import type { ModuleDefinition } from './moduleRegistry';
import type { Alert, ReportData } from '../tokens/types';
import { PdfSection, PdfChip } from '../primitives/PdfPrimitives';
import { ScreenSection, ScreenChip } from '../primitives/ScreenPrimitives';
import { fmtDate } from './formatUtils';
import { colors, fontFamily, severity, space, typeScale } from '../tokens/designTokens';
import type { Severity } from '../tokens/designTokens';

const SEV_ORDER: Severity[] = ['SEVERE', 'HIGH', 'MEDIUM', 'LOW'];

type Bucket = { severity: Severity; count: number; topCategories: Array<{ name: string; count: number }> };

const bucketize = (alerts: Alert[]): Bucket[] =>
    SEV_ORDER.map((sev) => {
        const matching = alerts.filter((a) => a.severity === sev);
        const catCounts = matching.reduce<Record<string, number>>((acc, a) => {
            const k = a.category || 'Uncategorised';
            acc[k] = (acc[k] ?? 0) + 1;
            return acc;
        }, {});
        const topCategories = Object.entries(catCounts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3)
            .map(([name, count]) => ({ name, count }));
        return { severity: sev, count: matching.length, topCategories };
    });

const sevSortRank = (s: Severity) => SEV_ORDER.indexOf(s);
const sortedAlerts = (alerts: Alert[]): Alert[] =>
    [...alerts].sort((a, b) => {
        const byRank = sevSortRank(a.severity) - sevSortRank(b.severity);
        if (byRank !== 0) return byRank;
        const ad = Date.parse(a.alertDate) || 0;
        const bd = Date.parse(b.alertDate) || 0;
        return bd - ad;
    });

// ---------- PDF ----------
const PdfSeverityBucket: React.FC<{ bucket: Bucket }> = ({ bucket }) => {
    const sev = severity[bucket.severity];
    return (
        <View
            style={{
                flex: 1,
                padding: space.sp3,
                borderRadius: 4,
                backgroundColor: sev.bg,
                borderWidth: 0.5,
                borderColor: sev.fg,
            }}
            wrap={false}
        >
            <Text
                style={{
                    fontSize: typeScale.micro,
                    color: sev.fg,
                    letterSpacing: 0.8,
                    textTransform: 'uppercase',
                    fontFamily: fontFamily.body,
                    fontWeight: 600,
                }}
            >
                {sev.label}
            </Text>
            <Text style={{ fontSize: 22, fontFamily: fontFamily.mono, fontWeight: 500, color: sev.fg, marginTop: 2, lineHeight: 1 }}>
                {bucket.count}
            </Text>
            <Text style={{ fontSize: typeScale.caption, color: colors.ink40, marginTop: 2 }}>
                {bucket.count === 1 ? 'alert raised' : 'alerts raised'}
            </Text>
        </View>
    );
};

const resolveEntityLabel = (a: Alert, assesseeName?: string): { name?: string; type?: string } => {
    if (a.entityName?.trim()) return { name: a.entityName.trim(), type: a.entityType };
    if (!a.source || a.source === 'COMPANY') return { name: assesseeName, type: 'Company' };
    return { name: undefined, type: a.entityType };
};

const PdfAlertCard: React.FC<{ a: Alert; assesseeName?: string }> = ({ a, assesseeName }) => {
    const sev = severity[a.severity];
    const entity = resolveEntityLabel(a, assesseeName);
    return (
        <View
            wrap={false}
            style={{
                borderWidth: 0.5,
                borderColor: colors.rule,
                borderLeftWidth: 2,
                borderLeftColor: sev.fg,
                borderRadius: 4,
                padding: space.sp3,
                backgroundColor: colors.paper,
                marginBottom: space.sp2,
            }}
        >
            {/* skill rule 9 corollary: chip + sibling label and mixed-font date need lineHeight:1 */}
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                    <PdfChip tone={a.severity}>{sev.label}</PdfChip>
                    <Text
                        style={{
                            fontSize: typeScale.micro,
                            color: colors.ink40,
                            letterSpacing: 0.6,
                            textTransform: 'uppercase',
                            fontFamily: fontFamily.body,
                            fontWeight: 600,
                            lineHeight: 1,
                        }}
                    >
                        {a.category || 'Alert'}
                    </Text>
                </View>
                <Text style={{ fontSize: typeScale.caption, color: colors.ink40, fontFamily: fontFamily.mono, lineHeight: 1 }}>
                    {fmtDate(a.alertDate)}
                </Text>
            </View>
            {entity.name ? (
                // skill rule 9 corollary: was alignItems:'baseline' with mixed sizes — drifted; use center + lineHeight:1
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                    <Text
                        style={{
                            fontSize: typeScale.h4Mini,
                            fontFamily: fontFamily.body,
                            fontWeight: 600,
                            color: colors.ink00,
                            lineHeight: 1,
                        }}
                    >
                        {entity.name}
                    </Text>
                    {entity.type ? (
                        <Text
                            style={{
                                fontSize: typeScale.micro,
                                color: colors.ink40,
                                letterSpacing: 0.4,
                                textTransform: 'uppercase',
                                lineHeight: 1,
                            }}
                        >
                            · {entity.type}
                        </Text>
                    ) : null}
                </View>
            ) : null}
            <Text style={{ fontSize: typeScale.bodySm, color: colors.ink10, lineHeight: 1.45 }}>
                {a.description && a.description.length > 280 ? a.description.slice(0, 280).trim() + '…' : a.description}
            </Text>
        </View>
    );
};

const PdfAlerts: React.FC<{ data: ReportData; index: string; breakBefore?: boolean }> = ({ data, index, breakBefore }) => {
    const a = data.alerts;
    if (!a) return null;
    const buckets = bucketize(a.alerts);
    const alerts = sortedAlerts(a.alerts).slice(0, 20);
    return (
        <PdfSection
            kicker="Signals"
            title="Alerts & anomalies"
            subtitle={`${a.totalRecords} total alert${a.totalRecords === 1 ? '' : 's'} raised across the assessee timeline`}
            index={index}
            breakBefore={breakBefore}
        >
            {/* skill rule 2: explicit row, never flexWrap. Four severity buckets always fit. */}
            <View style={{ flexDirection: 'row', gap: space.sp2, marginBottom: space.sp3 }} wrap={false}>
                {buckets.map((b) => (
                    <PdfSeverityBucket key={b.severity} bucket={b} />
                ))}
            </View>
            <View style={{ flexDirection: 'column' }}>
                {alerts.map((x, i) => (
                    <PdfAlertCard key={i} a={x} assesseeName={data.identity.orgName} />
                ))}
            </View>
        </PdfSection>
    );
};

// ---------- Screen ----------
const ScreenSeverityBucket: React.FC<{ bucket: Bucket }> = ({ bucket }) => {
    const sev = severity[bucket.severity];
    return (
        <div
            style={{
                flexGrow: 1,
                flexBasis: '22%',
                minWidth: 160,
                padding: 14,
                borderRadius: 4,
                background: sev.bg,
                border: `1px solid ${sev.fg}`,
            }}
        >
            <div style={{ fontSize: '0.7rem', color: sev.fg, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600 }}>
                {sev.label}
            </div>
            <div
                style={{
                    fontSize: '1.9rem',
                    fontFamily: "'Geist Mono', monospace",
                    fontWeight: 500,
                    color: sev.fg,
                    lineHeight: 1,
                    marginTop: 4,
                }}
            >
                {bucket.count}
            </div>
            <div style={{ fontSize: '0.72rem', color: colors.ink40, marginTop: 3 }}>
                {bucket.count === 1 ? 'alert raised' : 'alerts raised'}
            </div>
        </div>
    );
};

const ScreenAlertCard: React.FC<{ a: Alert; assesseeName?: string }> = ({ a, assesseeName }) => {
    const sev = severity[a.severity];
    const entity = resolveEntityLabel(a, assesseeName);
    return (
        <div
            style={{
                border: `1px solid ${colors.rule}`,
                borderLeft: `2px solid ${sev.fg}`,
                borderRadius: 4,
                padding: '12px 14px',
                background: colors.paper,
                marginBottom: 10,
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ScreenChip tone={a.severity}>{sev.label}</ScreenChip>
                    <span
                        style={{
                            fontSize: '0.7rem',
                            color: colors.ink40,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            fontWeight: 600,
                        }}
                    >
                        {a.category || 'Alert'}
                    </span>
                </div>
                <span style={{ fontSize: '0.75rem', color: colors.ink40, fontFamily: "'Geist Mono', monospace" }}>
                    {fmtDate(a.alertDate)}
                </span>
            </div>
            {entity.name ? (
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: '0.92rem', fontWeight: 600, color: colors.ink00 }}>{entity.name}</span>
                    {entity.type ? (
                        <span
                            style={{
                                fontSize: '0.66rem',
                                color: colors.ink40,
                                letterSpacing: '0.05em',
                                textTransform: 'uppercase',
                            }}
                        >
                            · {entity.type}
                        </span>
                    ) : null}
                </div>
            ) : null}
            <div style={{ fontSize: '0.88rem', color: colors.ink10, lineHeight: 1.5 }}>{a.description}</div>
        </div>
    );
};

const ScreenAlerts: React.FC<{ data: ReportData; index: string }> = ({ data, index }) => {
    const a = data.alerts;
    if (!a) return null;
    const buckets = bucketize(a.alerts);
    const alerts = sortedAlerts(a.alerts).slice(0, 20);
    return (
        <ScreenSection
            kicker="Signals"
            title="Alerts & anomalies"
            subtitle={`${a.totalRecords} total alert${a.totalRecords === 1 ? '' : 's'} raised across the assessee timeline`}
            index={index}
        >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                {buckets.map((b) => (
                    <ScreenSeverityBucket key={b.severity} bucket={b} />
                ))}
            </div>
            <div>
                {alerts.map((x, i) => (
                    <ScreenAlertCard key={i} a={x} assesseeName={data.identity.orgName} />
                ))}
            </div>
        </ScreenSection>
    );
};

export const alertsModule: ModuleDefinition = {
    meta: {
        id: 'alerts',
        title: 'Alerts & anomalies',
        description: 'Severity-bucketed alerts with category and reason per card.',
        dataSource: 'Alerts API',
    },
    hasData: (d) => !!d.alerts && d.alerts.alerts.length > 0,
    Pdf: PdfAlerts,
    Screen: ScreenAlerts,
};
